import {
  isProposalFeeScheduleAspect,
  proposalFeeScheduleAspectFromConflictKey,
  proposalFeeScheduleAspectLabels,
  proposalFeeScheduleAspectText,
  proposalFeeScheduleAspects,
  proposalFeeScheduleCategoriesPath,
  readProposalFeeScheduleCategoryValues,
  type ProposalFeeScheduleAspect,
  type ProposalFeeScheduleCategoryValues,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";

/**
 * A fee-schedule edit, as one thing that happened.
 *
 * The negotiation stream records a fee schedule the way it records everything: one event per semantic
 * leaf, so a conflict lands on a category's rate rather than on the schedule. A person editing the
 * schedule did not do that. Renaming a category, giving it a description and raising its rate is one
 * action; adding a category is one action; doing all of it in one save is one action still. Read back
 * leaf by leaf it became a wall of near-identical rows — "updated Fee Schedule Category: Drafting",
 * six times — with nothing to say which of them belonged together.
 *
 * This collapses the leaves back into the schedule they came from: one summary per save, one row per
 * category, and within a row the WHOLE category, with only the columns the actor touched blue-lined
 * (#4845). Showing the touched columns alone was the other half of the same problem — a reader saw
 * "Rate: $100 → $125" with no way to tell which category's rate that was without going and looking, and
 * an added or removed category showed one arbitrary column rather than the category that arrived or
 * left. A category that was added reads entirely as an insertion, one that was removed entirely as a
 * deletion, and one that was edited reads as itself with the edits marked inside it.
 *
 * Every surface that shows a fee-schedule change reads it from here — the change history and the
 * save/submit previews — so the schedule reads the same wherever the user meets it.
 */

export { proposalFeeScheduleCategoriesPath };

/** How a category itself changed, as against how one of its columns did. */
export type ProposalFeeScheduleCategoryAction = "added" | "removed" | "updated";

/**
 * One column of a category as the summary draws it.
 *
 * `changed` is what separates the two jobs this shape does: a changed column is the redline (previous
 * struck through, next in blue), an unchanged one is context (its current value, drawn plainly) so the
 * reader sees the category rather than a fragment of it.
 */
export type ProposalFeeScheduleAspectChange = {
  /** The canonical column, when the change names one. Absent for a leaf too old or too odd to place. */
  aspect?: ProposalFeeScheduleAspect;
  /**
   * What to call the column. Empty when the entry is about the whole category rather than one of its
   * columns — the restored summary's "Not included" — and the renderer then draws the value alone.
   */
  label: string;
  changed: boolean;
  previousText?: string;
  nextText?: string;
};

export type ProposalFeeScheduleCategoryChange = {
  /** Stable identity, so a category keeps one row however many leaves it contributed. */
  itemId: string;
  /** The category's name, when the change carries one. */
  itemLabel?: string;
  action: ProposalFeeScheduleCategoryAction;
  aspects: ProposalFeeScheduleAspectChange[];
};

export type ProposalFeeScheduleChangeSummary = {
  categories: ProposalFeeScheduleCategoryChange[];
};

/** The leaf shape every caller already has: one changed column of one category. */
export type ProposalFeeScheduleChangeInput = {
  itemId?: string | null;
  itemLabel?: string;
  /** The canonical column this leaf is about. Preferred over {@link fieldLabel}. */
  aspect?: ProposalFeeScheduleAspect;
  /** The leaf's own label, e.g. "Fee Schedule Category Rate". Used when no `aspect` is supplied. */
  fieldLabel?: string;
  action: ProposalFeeScheduleCategoryAction | string;
  previousText?: string;
  nextText?: string;
  /**
   * The category as a whole at the moment of this change, for the columns it did NOT touch. A caller
   * without it still gets the touched columns; it is the difference between reading a fragment and
   * reading the category.
   */
  categoryValues?: ProposalFeeScheduleCategoryValues;
};

const feeScheduleCategoryLabelPrefix = /^Fee Schedule Category\s*/;

const aspectsByLabel = new Map<string, ProposalFeeScheduleAspect>(
  proposalFeeScheduleAspects.map((aspect) => [proposalFeeScheduleAspectLabels[aspect], aspect]),
);

/** "Fee Schedule Category Rate" is a leaf of a category; the aspect is "Rate". */
export function proposalFeeScheduleAspectLabel(fieldLabel?: string): string {
  if (!fieldLabel) return "Category";
  return fieldLabel.replace(feeScheduleCategoryLabelPrefix, "").trim() || "Category";
}

/** Whether a change lands inside the fee schedule's category collection. */
export function isProposalFeeScheduleCategoryPath(path?: string | null): boolean {
  return path === proposalFeeScheduleCategoriesPath;
}

/**
 * Collapses a save's fee-schedule leaves into one summary.
 *
 * Categories keep the order their first leaf appeared in, so the summary reads in the order the actor
 * worked; columns within a category read in the schedule's own order rather than the stream's. A
 * category that was added or removed says so even when only some of its leaves carry that action —
 * adding a category emits an add per leaf, and one leaf is enough to know what happened to the whole.
 */
export function summarizeProposalFeeScheduleChanges(
  inputs: readonly ProposalFeeScheduleChangeInput[],
): ProposalFeeScheduleChangeSummary {
  const categoriesByItemId = new Map<string, CategoryAccumulator>();

  for (const input of inputs) {
    const itemId = input.itemId ?? "";
    let category = categoriesByItemId.get(itemId);
    if (!category) {
      category = { itemId, itemLabel: input.itemLabel, action: "updated", values: {}, changes: new Map(), extras: [] };
      categoriesByItemId.set(itemId, category);
    }
    category.itemLabel ??= input.itemLabel;
    if (input.action === "added" || input.action === "removed") {
      category.action = input.action;
    }
    // Later leaves of the same save fill in columns earlier ones did not carry, rather than replacing
    // what is already known: each leaf sees only its own column, so the category is the union of them.
    for (const [aspect, value] of Object.entries(input.categoryValues ?? {})) {
      if (isProposalFeeScheduleAspect(aspect) && !(aspect in category.values)) category.values[aspect] = value;
    }

    const aspect = input.aspect ?? aspectsByLabel.get(proposalFeeScheduleAspectLabel(input.fieldLabel));
    const label = aspect ? proposalFeeScheduleAspectLabels[aspect] : proposalFeeScheduleAspectLabel(input.fieldLabel);
    if (input.previousText === undefined && input.nextText === undefined) continue;

    if (!aspect) {
      // A leaf this vocabulary cannot place stays visible rather than being dropped: an unrecognised
      // column is still something the actor changed, and silence about it is the worse failure.
      if (!category.extras.some((extra) => extra.label === label)) {
        category.extras.push({
          label,
          changed: true,
          previousText: input.previousText,
          nextText: input.nextText,
        });
      }
      continue;
    }
    if (category.changes.has(aspect)) continue;
    category.changes.set(aspect, { previousText: input.previousText, nextText: input.nextText });
  }

  return { categories: [...categoriesByItemId.values()].map(renderCategory) };
}

type CategoryAccumulator = {
  itemId: string;
  itemLabel?: string;
  action: ProposalFeeScheduleCategoryAction;
  values: ProposalFeeScheduleCategoryValues;
  changes: Map<ProposalFeeScheduleAspect, { previousText?: string; nextText?: string }>;
  extras: ProposalFeeScheduleAspectChange[];
};

/**
 * Draws one category: every column it has something to say about, in the schedule's reading order.
 *
 * An added category is drawn entirely as an insertion and a removed one entirely as a deletion — the
 * whole thing arrived or left, so every column of it did. An edited category draws its edits as
 * redlines and everything else as it currently stands, which is what makes the row the category rather
 * than a list of the bits that moved.
 */
function renderCategory(category: CategoryAccumulator): ProposalFeeScheduleCategoryChange {
  const aspects: ProposalFeeScheduleAspectChange[] = [];

  for (const aspect of proposalFeeScheduleAspects) {
    const label = proposalFeeScheduleAspectLabels[aspect];
    const change = category.changes.get(aspect);
    const contextText = proposalFeeScheduleAspectText(aspect, category.values[aspect], category.values);

    if (category.action !== "updated") {
      const text = change?.[category.action === "added" ? "nextText" : "previousText"] ?? contextText;
      if (text === undefined) continue;
      aspects.push(
        category.action === "added"
          ? { aspect, label, changed: true, nextText: text }
          : { aspect, label, changed: true, previousText: text },
      );
      continue;
    }

    if (change) {
      aspects.push({ aspect, label, changed: true, previousText: change.previousText, nextText: change.nextText });
      continue;
    }
    if (contextText === undefined) continue;
    aspects.push({ aspect, label, changed: false, nextText: contextText });
  }

  return {
    itemId: category.itemId,
    itemLabel: category.itemLabel,
    action: category.action,
    // Unrecognised columns sort after the ones the schedule knows, keeping them visible without
    // letting them displace the reading order.
    aspects: [...aspects.filter((aspect) => !aspectOnlyRepeatsCategoryName(aspect, category.itemLabel)), ...category.extras],
  };
}

/**
 * Whether a column would only say again what the row's own heading says.
 *
 * The row is headed by the category's name, so listing an unchanged Name beside it printed "Trademark
 * Filing — Name: Trademark Filing". The same goes for an added or removed category, whose heading
 * already draws that name as an insertion or a deletion. A RENAME is the exception and stays: the
 * heading gives the name it ended up with, and the column is the only thing that says what it was.
 *
 * One rule, the same one `proposalChangeTransitionCarriesItemLabel` applies to a change line.
 */
function aspectOnlyRepeatsCategoryName(aspect: ProposalFeeScheduleAspectChange, itemLabel?: string): boolean {
  if (aspect.aspect !== "name" || itemLabel === undefined) return false;
  if (aspect.previousText === undefined) return aspect.nextText === itemLabel;
  return aspect.nextText === undefined && aspect.previousText === itemLabel;
}

/** The change descriptor the save and submit previews carry, as far as the fee schedule needs it. */
export type ProposalFeeScheduleChangeDescriptor = {
  /** The conflict key and the collection item, packed — see `proposalChangeKey`. */
  key: string;
  path: string;
  fieldLabel: string;
  itemLabel?: string;
  action: string;
  previousText?: string;
  nextText?: string;
  /** The whole category this change is one column of — see `ProposalChangeDescriptor.itemValue`. */
  itemValue?: unknown;
};

/**
 * The fee-schedule half of a change list, and everything else.
 *
 * The save and submit previews list one row per change, which for a fee schedule meant one row per
 * leaf — the same wall of near-identical rows the change history used to show. Splitting the list here
 * lets both previews hand the schedule to {@link summarizeProposalFeeScheduleChanges} and keep their
 * ordinary rendering for everything else, without either of them deciding for itself what counts as a
 * fee-schedule change.
 */
export function partitionProposalFeeScheduleChanges<T extends { path: string }>(
  changes: readonly T[],
): { feeScheduleChanges: T[]; otherChanges: T[] } {
  const feeScheduleChanges: T[] = [];
  const otherChanges: T[] = [];
  for (const change of changes) {
    (isProposalFeeScheduleCategoryPath(change.path) ? feeScheduleChanges : otherChanges).push(change);
  }
  return { feeScheduleChanges, otherChanges };
}

/** Collapses a preview's fee-schedule descriptors into the same summary the change history renders. */
export function summarizeProposalFeeScheduleDescriptors(
  descriptors: readonly ProposalFeeScheduleChangeDescriptor[],
): ProposalFeeScheduleChangeSummary {
  return summarizeProposalFeeScheduleChanges(
    descriptors.map((descriptor) => {
      // The descriptor packs the conflict key and the item into one identity; the conflict key names the
      // column, the item is the category.
      const [conflictKey, itemId] = descriptor.key.split("|");
      return {
        itemId: itemId ?? descriptor.key,
        itemLabel: descriptor.itemLabel,
        aspect: proposalFeeScheduleAspectFromConflictKey(conflictKey),
        fieldLabel: descriptor.fieldLabel,
        action: descriptor.action,
        previousText: descriptor.previousText,
        nextText: descriptor.nextText,
        categoryValues: readProposalFeeScheduleCategoryValues(descriptor.itemValue),
      };
    }),
  );
}
